import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShoppingBag, Plus, Minus, Trash2, Sparkles, Tag, X, Truck, Store, Gift } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { EmbeddedCheckoutPanel } from "../EmbeddedCheckoutPanel";
import api, { formatApiError } from "../../lib/api";

export function ShopTab({ user }) {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({}); // slug -> qty
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const [giftInput, setGiftInput] = useState("");
  const [giftCard, setGiftCard] = useState(null);
  const [giftBusy, setGiftBusy] = useState(false);
  const [fulfillment, setFulfillment] = useState("pickup");
  const [addr, setAddr] = useState({ name: "", line1: "", line2: "", city: "", state: "", zip: "", phone: "" });
  const isMember = user?.tier && user.tier !== "none";

  useEffect(() => {
    if (user?.shipping_address) setAddr((a) => ({ ...a, ...user.shipping_address }));
    else if (user?.name) setAddr((a) => ({ ...a, name: a.name || user.name }));
  }, [user]);

  useEffect(() => {
    api.get("/products?all=true").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const add = (slug) => setCart((c) => ({ ...c, [slug]: (c[slug] || 0) + 1 }));
  const dec = (slug) =>
    setCart((c) => {
      const q = (c[slug] || 0) - 1;
      const next = { ...c };
      if (q <= 0) delete next[slug];
      else next[slug] = q;
      return next;
    });
  const removeItem = (slug) => setCart((c) => { const n = { ...c }; delete n[slug]; return n; });

  const priceOf = (p) => (isMember ? p.member_price : p.price);
  const cartEntries = Object.entries(cart);
  const cartTotal = cartEntries.reduce((sum, [slug, qty]) => {
    const p = products.find((x) => x.slug === slug);
    return sum + (p ? priceOf(p) * qty : 0);
  }, 0);
  const cartSavings = isMember
    ? cartEntries.reduce((sum, [slug, qty]) => {
        const p = products.find((x) => x.slug === slug);
        return sum + (p ? (p.price - p.member_price) * qty : 0);
      }, 0)
    : 0;

  const promoDiscount = appliedPromo
    ? appliedPromo.discount_type === "percent"
      ? (cartTotal * appliedPromo.value) / 100
      : Math.min(appliedPromo.value, cartTotal)
    : 0;
  const finalTotal = Math.max(cartTotal - promoDiscount, cartTotal > 0 ? 0.5 : 0);
  const shippingFee = fulfillment === "ship" ? 15 : 0;
  const afterPromo = finalTotal + shippingFee;
  const giftApplied = giftCard ? Math.min(giftCard.balance, afterPromo) : 0;
  const grandTotal = Math.max(afterPromo - giftApplied, 0);

  const applyGift = async () => {
    const code = giftInput.trim().toUpperCase();
    if (!code) return;
    setGiftBusy(true);
    try {
      const { data } = await api.get(`/giftcards/balance/${code}`);
      setGiftCard(data);
      toast.success(`Gift card applied — $${data.balance.toFixed(2)} available`);
    } catch (e) {
      setGiftCard(null);
      toast.error(formatApiError(e.response?.data?.detail) || "Invalid gift card");
    } finally { setGiftBusy(false); }
  };
  const clearGift = () => { setGiftCard(null); setGiftInput(""); };
  const addrComplete = ["name", "line1", "city", "state", "zip"].every((k) => (addr[k] || "").trim());

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setPromoBusy(true);
    try {
      const { data } = await api.post("/member/validate-promo", { code });
      setAppliedPromo(data);
      const off = data.discount_type === "percent" ? `${data.value}% off` : `$${data.value} off`;
      toast.success(`Promo ${data.code} applied — ${off}`);
    } catch (e) {
      setAppliedPromo(null);
      toast.error(formatApiError(e.response?.data?.detail) || "Invalid promo code");
    } finally {
      setPromoBusy(false);
    }
  };

  const clearPromo = () => { setAppliedPromo(null); setPromoInput(""); };

  const checkout = async () => {
    setLoading(true);
    try {
      const items = cartEntries.map(([slug, quantity]) => ({ slug, quantity }));
      const { data } = await api.post("/checkout/session", {
        items,
        origin_url: window.location.origin,
        promo_code: appliedPromo?.code || null,
        gift_card_code: giftCard?.code || null,
        fulfillment,
        shipping_address: fulfillment === "ship" ? addr : null,
      });
      if (data.fully_covered) { window.location.href = `${window.location.origin}/dashboard?tab=orders`; return; }
      setClientSecret(data.client_secret);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
      setLoading(false);
    }
  };

  if (clientSecret) {
    return (
      <div data-testid="shop-tab">
        <div className="mb-6">
          <h2 className="font-heading text-2xl font-light text-ink">Complete your purchase</h2>
          <p className="text-sm text-[#8A9492] mt-1">Secure payment — you never leave our site.</p>
        </div>
        <div className="max-w-2xl">
          <EmbeddedCheckoutPanel clientSecret={clientSecret} onBack={() => { setClientSecret(null); setLoading(false); }} />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="shop-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-light text-ink">Shop skincare</h2>
          <p className="text-sm text-[#8A9492] mt-1">
            {isMember ? "Member pricing applied to your cart." : "Join a membership to unlock member pricing."}
          </p>
        </div>
      </div>

      {cartEntries.length > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-slate-100 mb-6" data-testid="cart-summary">
          <h3 className="font-heading text-lg font-medium text-ink mb-4 flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-iri-mid" /> Your cart
          </h3>
          <ul className="divide-y divide-slate-100">
            {cartEntries.map(([slug, qty]) => {
              const p = products.find((x) => x.slug === slug);
              if (!p) return null;
              return (
                <li key={slug} className="py-3 flex items-center gap-4" data-testid={`cart-item-${slug}`}>
                  <img src={p.image} alt={p.name} className="h-12 w-12 rounded-lg object-cover" />
                  <div className="flex-1">
                    <p className="text-ink font-medium text-sm">{p.name}</p>
                    <p className="text-xs text-[#8A9492]">${priceOf(p).toFixed(2)} each</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => dec(slug)} className="h-7 w-7 rounded-full border border-slate-200 flex items-center justify-center hover:bg-stone" data-testid={`cart-dec-${slug}`}><Minus className="h-3.5 w-3.5" /></button>
                    <span className="w-6 text-center text-sm" data-testid={`cart-qty-${slug}`}>{qty}</span>
                    <button onClick={() => add(slug)} className="h-7 w-7 rounded-full border border-slate-200 flex items-center justify-center hover:bg-stone" data-testid={`cart-inc-${slug}`}><Plus className="h-3.5 w-3.5" /></button>
                    <button onClick={() => removeItem(slug)} className="ml-2 text-[#8A9492] hover:text-red-500" data-testid={`cart-remove-${slug}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </li>
              );
            })}
          </ul>

          {/* Promo code */}
          <div className="mt-5 pt-4 border-t border-slate-100" data-testid="shop-promo-box">
            {appliedPromo ? (
              <div className="flex items-center justify-between gap-3 rounded-xl bg-stone px-4 py-2.5" data-testid="shop-promo-applied">
                <span className="text-sm text-ink flex items-center gap-2">
                  <Tag className="h-4 w-4 text-iri-mid" /> <span className="font-mono font-medium">{appliedPromo.code}</span> applied
                </span>
                <button onClick={clearPromo} data-testid="shop-promo-clear" className="text-[#8A9492] hover:text-red-500 transition-colors"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  placeholder="Promo code"
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && applyPromo()}
                  data-testid="shop-promo-input"
                  className="rounded-full h-11 uppercase"
                />
                <Button onClick={applyPromo} disabled={promoBusy || !promoInput.trim()} data-testid="shop-promo-apply" className="rounded-full h-11 px-6 bg-ink text-white hover:bg-ink/90 shrink-0">
                  {promoBusy ? "…" : "Apply"}
                </Button>
              </div>
            )}
          </div>

          {/* Gift card */}
          <div className="mt-3" data-testid="shop-gift-box">
            {giftCard ? (
              <div className="flex items-center justify-between gap-3 rounded-xl bg-stone px-4 py-2.5" data-testid="shop-gift-applied">
                <span className="text-sm text-ink flex items-center gap-2">
                  <Gift className="h-4 w-4 text-iri-mid" /> <span className="font-mono font-medium">{giftCard.code}</span> · ${giftCard.balance.toFixed(2)}
                </span>
                <button onClick={clearGift} data-testid="shop-gift-clear" className="text-[#8A9492] hover:text-red-500 transition-colors"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input placeholder="Gift card code" value={giftInput} onChange={(e) => setGiftInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && applyGift()} data-testid="shop-gift-input" className="rounded-full h-11 uppercase" />
                <Button onClick={applyGift} disabled={giftBusy || !giftInput.trim()} data-testid="shop-gift-apply" className="rounded-full h-11 px-6 bg-ink text-white hover:bg-ink/90 shrink-0">
                  {giftBusy ? "…" : "Apply"}
                </Button>
              </div>
            )}
          </div>

          {/* Fulfillment */}
          <div className="mt-5 pt-4 border-t border-slate-100" data-testid="fulfillment-section">
            <p className="text-sm font-medium text-ink mb-3">How would you like to receive your order?</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFulfillment("pickup")}
                data-testid="fulfillment-pickup"
                className={`rounded-xl border p-4 text-left transition-colors ${fulfillment === "pickup" ? "border-iri-mid bg-stone" : "border-slate-200 hover:border-slate-300"}`}
              >
                <Store className={`h-5 w-5 mb-2 ${fulfillment === "pickup" ? "text-iri-mid" : "text-[#8A9492]"}`} />
                <p className="text-sm font-medium text-ink">Pick up in spa</p>
                <p className="text-xs text-[#8A9492] mt-0.5">Free · ready at reception</p>
              </button>
              <button
                type="button"
                onClick={() => setFulfillment("ship")}
                data-testid="fulfillment-ship"
                className={`rounded-xl border p-4 text-left transition-colors ${fulfillment === "ship" ? "border-iri-mid bg-stone" : "border-slate-200 hover:border-slate-300"}`}
              >
                <Truck className={`h-5 w-5 mb-2 ${fulfillment === "ship" ? "text-iri-mid" : "text-[#8A9492]"}`} />
                <p className="text-sm font-medium text-ink">Ship to me</p>
                <p className="text-xs text-[#8A9492] mt-0.5">+$15 shipping &amp; handling</p>
              </button>
            </div>

            {fulfillment === "ship" && (
              <div className="mt-4 space-y-3" data-testid="shipping-address-form">
                {user?.shipping_address && (
                  <p className="text-xs text-[#8A9492]">We prefilled the address on file — edit below to ship somewhere new.</p>
                )}
                <Input placeholder="Full name" value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} data-testid="ship-name" className="rounded-lg h-11" />
                <Input placeholder="Street address" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} data-testid="ship-line1" className="rounded-lg h-11" />
                <Input placeholder="Apt / suite (optional)" value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} data-testid="ship-line2" className="rounded-lg h-11" />
                <div className="grid grid-cols-3 gap-3">
                  <Input placeholder="City" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid="ship-city" className="rounded-lg h-11" />
                  <Input placeholder="State" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} data-testid="ship-state" className="rounded-lg h-11" />
                  <Input placeholder="ZIP" value={addr.zip} onChange={(e) => setAddr({ ...addr, zip: e.target.value })} data-testid="ship-zip" className="rounded-lg h-11" />
                </div>
                <Input placeholder="Phone (optional)" value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} data-testid="ship-phone" className="rounded-lg h-11" />
                {!addrComplete && <p className="text-xs text-amber-600" data-testid="ship-incomplete">Please complete name, street, city, state and ZIP to ship.</p>}
              </div>
            )}
          </div>

          {/* Totals breakdown */}
          <div className="mt-5 pt-4 border-t border-slate-100 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#5C6664]">Subtotal</span>
              <span className="text-ink" data-testid="cart-total">${cartTotal.toFixed(2)}</span>
            </div>
            {appliedPromo && (
              <div className="flex items-center justify-between text-sm" data-testid="cart-promo-line">
                <span className="text-iri-mid">Promo {appliedPromo.code}</span>
                <span className="text-iri-mid">−${promoDiscount.toFixed(2)}</span>
              </div>
            )}
            {shippingFee > 0 && (
              <div className="flex items-center justify-between text-sm" data-testid="cart-shipping-line">
                <span className="text-[#5C6664]">Shipping &amp; handling</span>
                <span className="text-ink">+${shippingFee.toFixed(2)}</span>
              </div>
            )}
            {giftApplied > 0 && (
              <div className="flex items-center justify-between text-sm" data-testid="cart-gift-line">
                <span className="text-iri-mid">Gift card {giftCard.code}</span>
                <span className="text-iri-mid">−${giftApplied.toFixed(2)}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <span className="text-ink font-medium">Total</span>
              <span className="font-heading text-2xl font-light text-ink" data-testid="cart-grand-total">${grandTotal.toFixed(2)}</span>
            </div>
          </div>

          {cartSavings > 0 && (
            <div className="flex items-center justify-center gap-1.5 mt-3 text-sm iri-gradient text-ink rounded-full py-2 font-medium" data-testid="cart-savings-line">
              <Sparkles className="h-4 w-4" /> You saved ${cartSavings.toFixed(2)} as a member
            </div>
          )}
          <Button
            onClick={checkout}
            disabled={loading || (fulfillment === "ship" && !addrComplete)}
            data-testid="checkout-button"
            className="mt-4 w-full iri-gradient text-ink rounded-full h-12 font-medium hover:scale-[1.01] transition-transform"
          >
            {loading ? "Starting secure checkout…" : "Checkout securely"}
          </Button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {products.map((p) => {
          const oos = p.in_stock === false;
          return (
          <div key={p.slug} className={`bg-white rounded-2xl overflow-hidden border border-slate-100 hover-lift flex flex-col ${oos ? "opacity-75" : ""}`} data-testid={`product-card-${p.slug}`}>
            <div className="img-zoom aspect-square overflow-hidden bg-stone relative">
              <img src={p.image} alt={p.name} className={`w-full h-full object-cover ${oos ? "grayscale" : ""}`} />
              {oos && (
                <span className="absolute top-3 left-3 bg-ink/85 text-white text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full" data-testid={`oos-badge-${p.slug}`}>
                  Out of stock
                </span>
              )}
            </div>
            <div className="p-5 flex flex-col flex-1">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A9492]">{p.category}</span>
              <h3 className="font-heading text-base font-medium text-ink mt-1">{p.name}</h3>
              <p className="text-xs text-[#5C6664] mt-1 flex-1">{p.tagline}</p>
              <div className="flex items-center gap-2 mt-3">
                {isMember && p.member_price < p.price ? (
                  <>
                    <span className="text-ink font-medium">${p.member_price.toFixed(2)}</span>
                    <span className="text-xs text-[#8A9492] line-through">${p.price.toFixed(2)}</span>
                    <span className="text-[10px] iri-gradient text-ink px-2 py-0.5 rounded-full font-medium">Member</span>
                  </>
                ) : (
                  <span className="text-ink font-medium">${p.price.toFixed(2)}</span>
                )}
              </div>
              <Button
                onClick={() => add(p.slug)}
                disabled={oos}
                data-testid={`add-to-cart-${p.slug}`}
                className={`mt-4 rounded-full h-10 ${oos ? "bg-slate-100 text-[#8A9492] cursor-not-allowed hover:bg-slate-100" : "bg-ink text-white hover:bg-ink/90"}`}
              >
                {oos ? "Out of stock" : "Add to cart"}
              </Button>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}
